import { useMemo, useCallback } from "react";

import { useOptimizedBreakpoint } from "@brexhq/next/src/hooks/useOptimizedBreakpoint";

import { convertSpacersToNumericArray } from "../../../layout/Spacers/Spacers";
import { FormSettingsProps } from "../LeadformController";
import { Fields } from "../sharedTypes";

export const useOverrideFieldMargin = (formSettings?: FormSettingsProps) => {
  const { currentBreakpoint } = useOptimizedBreakpoint();

  // Converts the form setting field gap to a numeric value to decide whether to add additional margin to the field
  const numericFieldGap = useMemo<number | null>(() => {
    if (formSettings?.fieldGap == null) {
      return null;
    }
    const numericValue = convertSpacersToNumericArray(
      formSettings?.fieldGap,
      currentBreakpoint,
    )[0];
    if (typeof numericValue === "number") {
      return numericValue;
    }
    return null;
  }, [formSettings?.fieldGap, currentBreakpoint]);

  const defaultFieldGap = useMemo(() => {
    const defaultValue = convertSpacersToNumericArray(
      "sml",
      currentBreakpoint,
    )[0];
    if (typeof defaultValue === "number") {
      return defaultValue;
    } else if (typeof defaultValue === "string") {
      try {
        return parseInt(defaultValue, 10);
      } catch (error) {
        console.error("Error converting default margin to number", error);
        return 24;
      }
    }
    return 24;
  }, [currentBreakpoint]);

  // Adds additional margin to the field if the field error and the numeric field gap is less than or equal to 24 (standard field gap)
  const getAdditionalFieldMargin = useCallback(
    (field: Fields[keyof Fields], fallback?: string) => {
      if (
        field.error &&
        numericFieldGap != null &&
        numericFieldGap <= defaultFieldGap
      ) {
        return { marginBottom: `${defaultFieldGap}px` };
      }
      if (fallback) {
        return { marginBottom: fallback };
      }
      return {};
    },
    [numericFieldGap, defaultFieldGap],
  );

  return {
    getAdditionalFieldMargin,
  };
};
